import React, { useEffect } from "react";
import { IChat, IMessageDirection } from "../../../models/Chat.model";
import {
  DoubleTickIcon,
  FailedIcon,
  SingleTickIcon,
} from "../../../utils/ChatIcons";
import { IMessageStatus } from "../../../models/whatsApp.model";
import socket from "../../../utils/socketConnect.utils";
import { PaperClipIcon } from "@heroicons/react/24/outline";


interface ChatBubbleProps {
  msg: IChat;
}

const ChatBubble: React.FC<ChatBubbleProps> = ({ msg }) => {
  const [status, setStatus] = React.useState<string>("");

  useEffect(() => {
    setStatus(msg?.status || IMessageStatus.Sent);
  }, [msg?.status]);

  const isIncoming = msg?.direction === IMessageDirection.Receiving;
  if (!socket.connected) socket.connect();
  socket.on(msg?.messageId ?? "", (recentStatus) => {
    if (status !== IMessageStatus.Read) setStatus(recentStatus);
  });


const renderAttachment = (url: string, index: number) => {
  const extension = url.split('.').pop()?.toLowerCase();
  const isImage = ['jpg', 'jpeg', 'png', 'gif'].includes(extension || '');
  const isVideo = ['mp4', 'mov'].includes(extension || '');
  const isDocument = ['pdf', 'doc', 'docx'].includes(extension || '');

  if (isImage) {
    return (
      <div key={index} className="mt-2">
        <img 
          src={url}
          alt={`Attachment ${index}`}
          className="max-w-xs max-h-64 object-contain rounded-lg"
          
        />
      </div>
    );
  }

  if (isVideo) {
    return (
      <div key={index} className="mt-2">
        <video controls className="max-w-xs max-h-64 rounded-lg">
          <source src={url} type={`video/${extension}`} />
          Your browser does not support the video tag.
        </video>
      </div>
    );
  }

  if (isDocument) {
    return (
      <div key={index} className="mt-2">
        <div className="p-3 border rounded-lg bg-gray-50 flex items-center">
          <PaperClipIcon className="h-5 w-5 text-gray-500 mr-2" />
          <a 
            href={url}
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-500 hover:underline truncate"
          >
            Document {index + 1}
          </a>
        </div>
      </div>
    );
  }
}



  return (
    <div className={`flex ${isIncoming ? "justify-start" : "justify-end"}`}>
      <div
        className={`relative px-4 py-4 rounded-lg shadow-sm ${
          isIncoming
            ? "bg-white text-black rounded-bl-none max-w-[70%]"
            : "bg-[#dcf8c6] text-black rounded-br-none max-w-[70%]"
        }`}
      >
        <div className="flex items-center justify-between gap-2 mb-1">
          {!isIncoming && msg?.sender?.name && (
            <span className="text-xs  text-gray-700">
              {msg?.sender?.name || "Unknown"}
            </span>
          )}
          <span className="text-[0.65rem] text-gray-500 ml-auto whitespace-nowrap">
            {new Date(msg?.createdAt).toLocaleTimeString()}
          </span>
        </div>

        {msg?.message && (
          <div className="text-sm mb-1 break-words">{msg?.message}</div>
        )}

         {msg?.attachments?.map((url, index) => (
  renderAttachment(url, index)
))}

        {msg?.direction === IMessageDirection.Sending && (
          <div className="absolute bottom-[1px] right-2 flex items-center gap-1">
            {status === IMessageStatus.Failed ? (
              <div className="flex items-center gap-1">
                <FailedIcon />
              </div>
            ) : status === IMessageStatus.Sent ? (
              <SingleTickIcon />
            ) : status === IMessageStatus.Delivered ? (
              <SingleTickIcon />
            ) : status === IMessageStatus.Read ? (
              <DoubleTickIcon color="blue" />
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatBubble;
